import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deviceAPI, userAPI, documentAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  UserPlusIcon,
  UserMinusIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  ArrowsRightLeftIcon,
  QrCodeIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const DeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showSoftwareModal, setShowSoftwareModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadData, setUploadData] = useState({ file: null, documentType: 'assignment', description: '' });
  const [uploading, setUploading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [assignData, setAssignData] = useState({ userId: '', assignmentDate: '', description: '' });
  const [unassignData, setUnassignData] = useState({ reason: '', description: '' });
  const [transferData, setTransferData] = useState({
    newUserId: '',
    reason: '',
    description: '',
    transferDate: new Date().toISOString().split('T')[0],
    documentFile: null
  });
  const [softwareData, setSoftwareData] = useState({ name: '', version: '', licenseKey: '', installDate: '' });
  const [showQRModal, setShowQRModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const qrRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    fetchDevice();
    fetchUsers();
    fetchDocuments();
  }, [id]);

  const fetchDevice = async () => {
    try {
      const response = await deviceAPI.getById(id);
      setDevice(response.data);
    } catch (error) {
      toast.error('Cihaz yüklenirken hata oluştu');
      navigate('/devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll({ isActive: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.getByDevice(id);
      setDocuments(response.data);
    } catch (error) {
      console.error('Documents fetch error:', error);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error('Lütfen bir dosya seçin');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadData.file);
      formData.append('documentType', uploadData.documentType);
      formData.append('description', uploadData.description);

      await documentAPI.upload(id, formData);
      toast.success('Belge yüklendi');
      setShowUploadModal(false);
      setUploadData({ file: null, documentType: 'assignment', description: '' });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Belge yüklenirken hata');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await documentAPI.download(doc.id);
      const blob = new Blob([response.data], { type: doc.mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Belge indirilemedi');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Bu belgeyi silmek istediğinize emin misiniz?')) return;
    try {
      await documentAPI.delete(docId);
      toast.success('Belge silindi');
      fetchDocuments();
    } catch (error) {
      toast.error('Belge silinirken hata');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await deviceAPI.getPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zimmet_${device.serialNumber || device.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Zimmet PDF oluşturuldu');
    } catch (error) {
      toast.error(error.response?.data?.error || 'PDF oluşturulurken hata');
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr_${device.serialNumber || device.id}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrintLabel = () => {
    const printWindow = window.open('', '_blank');
    const qrSvg = labelRef.current?.querySelector('svg');
    const qrData = qrSvg ? new XMLSerializer().serializeToString(qrSvg) : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cihaz Etiketi - ${device.name}</title>
        <style>
          @page { size: A4 landscape; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 50%, #1b263b 100%);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: white;
            position: relative;
            overflow: hidden;
          }
          /* Circuit pattern overlay */
          .circuit-pattern {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0.1;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Ccircle cx='10' cy='10' r='2' fill='white'/%3E%3Ccircle cx='50' cy='10' r='2' fill='white'/%3E%3Ccircle cx='90' cy='10' r='2' fill='white'/%3E%3Ccircle cx='10' cy='50' r='2' fill='white'/%3E%3Ccircle cx='50' cy='50' r='3' fill='white'/%3E%3Ccircle cx='90' cy='50' r='2' fill='white'/%3E%3Ccircle cx='10' cy='90' r='2' fill='white'/%3E%3Ccircle cx='50' cy='90' r='2' fill='white'/%3E%3Ccircle cx='90' cy='90' r='2' fill='white'/%3E%3Cpath d='M10 10 L50 10 M50 10 L50 50 M50 50 L90 50 M10 50 L10 90 M90 50 L90 90 M10 90 L50 90' stroke='white' stroke-width='1' fill='none'/%3E%3C/svg%3E");
          }
          /* Logo area top right */
          .logo-area {
            position: absolute;
            top: 30px;
            right: 40px;
            text-align: right;
          }
          .logo-area h1 {
            font-size: 28px;
            font-weight: bold;
            color: #60a5fa;
            text-shadow: 0 2px 10px rgba(96, 165, 250, 0.5);
          }
          .logo-area p {
            font-size: 14px;
            color: #94a3b8;
            margin-top: 5px;
          }
          /* Main content center */
          .main-content {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            text-align: center;
          }
          .device-name {
            font-size: 48px;
            font-weight: bold;
            color: white;
            text-shadow: 0 4px 20px rgba(0,0,0,0.5);
            margin-bottom: 10px;
          }
          .device-model {
            font-size: 24px;
            color: #94a3b8;
            margin-bottom: 30px;
          }
          .specs-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 30px;
            margin-top: 20px;
          }
          .spec-item {
            background: rgba(255,255,255,0.1);
            border-radius: 12px;
            padding: 20px;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.1);
          }
          .spec-label {
            font-size: 12px;
            color: #60a5fa;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          .spec-value {
            font-size: 18px;
            font-weight: bold;
            margin-top: 5px;
          }
          /* QR Code area bottom left */
          .qr-area {
            position: absolute;
            bottom: 30px;
            left: 40px;
            display: flex;
            align-items: flex-end;
            gap: 20px;
          }
          .qr-code {
            background: white;
            padding: 10px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          }
          .qr-code svg {
            display: block;
            width: 120px;
            height: 120px;
          }
          .qr-info {
            color: #94a3b8;
            font-size: 12px;
          }
          .qr-info p {
            margin: 3px 0;
          }
          .qr-info .label {
            color: #60a5fa;
            font-size: 10px;
            text-transform: uppercase;
          }
          /* User info bottom right */
          .user-area {
            position: absolute;
            bottom: 30px;
            right: 40px;
            text-align: right;
          }
          .user-label {
            font-size: 12px;
            color: #60a5fa;
            text-transform: uppercase;
          }
          .user-name {
            font-size: 24px;
            font-weight: bold;
            color: white;
          }
          .user-dept {
            font-size: 14px;
            color: #94a3b8;
          }
          /* Decorative elements */
          .glow-orb {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.3;
          }
          .glow-1 {
            width: 300px;
            height: 300px;
            background: #3b82f6;
            top: -100px;
            right: 20%;
          }
          .glow-2 {
            width: 200px;
            height: 200px;
            background: #8b5cf6;
            bottom: 10%;
            left: 10%;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="circuit-pattern"></div>
        <div class="glow-orb glow-1"></div>
        <div class="glow-orb glow-2"></div>

        <div class="logo-area">
          <h1>IT Envanter Sistemi</h1>
          <p>Bilgi Islem Departmani</p>
        </div>

        <div class="main-content">
          <div class="device-name">${device.name}</div>
          <div class="device-model">${device.brand} ${device.model}</div>

          <div class="specs-grid">
            ${device.processor ? `
            <div class="spec-item">
              <div class="spec-label">Islemci</div>
              <div class="spec-value">${device.processor}${device.processorGeneration ? ` (${device.processorGeneration}. Nesil)` : ''}</div>
            </div>
            ` : ''}
            ${device.ram ? `
            <div class="spec-item">
              <div class="spec-label">RAM</div>
              <div class="spec-value">${device.ram}</div>
            </div>
            ` : ''}
            ${device.storage ? `
            <div class="spec-item">
              <div class="spec-label">Depolama</div>
              <div class="spec-value">${device.storage}</div>
            </div>
            ` : ''}
          </div>
        </div>

        <div class="qr-area">
          <div class="qr-code">${qrData}</div>
          <div class="qr-info">
            <p class="label">Seri No</p>
            <p>${device.serialNumber || '-'}</p>
            <p class="label" style="margin-top: 8px;">Barkod</p>
            <p>${device.barcode || '-'}</p>
            <p class="label" style="margin-top: 8px;">IP Adresi</p>
            <p>${device.ipAddress || 'DHCP'}</p>
            <p class="label" style="margin-top: 8px;">Hostname</p>
            <p>${device.hostname || device.name}</p>
          </div>
        </div>

        ${device.assignedUser ? `
        <div class="user-area">
          <div class="user-label">Zimmetli</div>
          <div class="user-name">${device.assignedUser.fullName}</div>
          <div class="user-dept">${device.assignedUser.department || ''} ${device.assignedUser.location ? '- ' + device.assignedUser.location : ''}</div>
        </div>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await deviceAPI.assign(id, assignData);
      toast.success('Cihaz zimmetlendi');
      setShowAssignModal(false);
      setAssignData({ userId: '', assignmentDate: '', description: '' });
      fetchDevice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Zimmetleme başarısız');
    }
  };

  const handleUnassign = async (e) => {
    e.preventDefault();
    try {
      await deviceAPI.unassign(id, unassignData);
      toast.success('Cihaz zimmetten düşürüldü');
      setShowUnassignModal(false);
      setUnassignData({ reason: '', description: '' });
      fetchDevice();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleAddSoftware = async (e) => {
    e.preventDefault();
    try {
      await deviceAPI.addSoftware(id, softwareData);
      toast.success('Yazılım eklendi');
      setShowSoftwareModal(false);
      setSoftwareData({ name: '', version: '', licenseKey: '', installDate: '' });
      fetchDevice();
    } catch (error) {
      toast.error('Yazılım eklenirken hata');
    }
  };

  const handleRemoveSoftware = async (softwareId) => {
    if (!window.confirm('Yazılımı silmek istediğinize emin misiniz?')) return;
    try {
      await deviceAPI.removeSoftware(id, softwareId);
      toast.success('Yazılım silindi');
      fetchDevice();
    } catch (error) {
      toast.error('Yazılım silinirken hata');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);
    try {
      // Önce yeni kullanıcıya zimmetle
      await deviceAPI.assign(id, {
        userId: transferData.newUserId,
        assignmentDate: transferData.transferDate,
        description: `Devir sebebi: ${transferData.reason}${transferData.description ? ' - ' + transferData.description : ''}`
      });

      // Eğer belge seçildiyse yükle
      if (transferData.documentFile) {
        const formData = new FormData();
        formData.append('document', transferData.documentFile);
        formData.append('documentType', 'assignment');
        formData.append('description', `Zimmet devir belgesi - ${transferData.reason}`);
        await documentAPI.upload(id, formData);
      }

      toast.success('Zimmet devri tamamlandı');
      setShowTransferModal(false);
      setTransferData({
        newUserId: '',
        reason: '',
        description: '',
        transferDate: new Date().toISOString().split('T')[0],
        documentFile: null
      });
      fetchDevice();
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Zimmet devri başarısız');
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!device) return null;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/devices')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{device.name}</h1>
            <p className="text-gray-500">{device.brand} {device.model}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowQRModal(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <QrCodeIcon className="h-5 w-5 mr-2" />
            QR Kod
          </button>
          {device.assignedUser && (
            <button
              onClick={handleDownloadPDF}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Zimmet PDF
            </button>
          )}
          <button
            onClick={() => navigate(`/devices/${id}/edit`)}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Duzenle
          </button>
          {device.assignedUser ? (
            <>
              <button
                onClick={() => setShowTransferModal(true)}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
                Zimmet Devret
              </button>
              <button
                onClick={() => setShowUnassignModal(true)}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <UserMinusIcon className="h-5 w-5 mr-2" />
                Zimmetten Düş
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowAssignModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <UserPlusIcon className="h-5 w-5 mr-2" />
              Zimmetle
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Cihaz Bilgileri</h2>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Cihaz Türü</dt>
              <dd className="text-sm font-medium">{device.deviceType?.name}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Seri No</dt>
              <dd className="text-sm font-medium">{device.serialNumber || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Barkod</dt>
              <dd className="text-sm font-medium">{device.barcode || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Durum</dt>
              <dd className="text-sm font-medium capitalize">{device.status}</dd>
            </div>
            {device.processor && (
              <>
                <div>
                  <dt className="text-sm text-gray-500">İşlemci</dt>
                  <dd className="text-sm font-medium">{device.processor}</dd>
                </div>
                {device.processorGeneration && (
                  <div>
                    <dt className="text-sm text-gray-500">İşlemci Nesli</dt>
                    <dd className="text-sm font-medium">{device.processorGeneration}. Nesil</dd>
                  </div>
                )}
              </>
            )}
            {device.ram && (
              <div>
                <dt className="text-sm text-gray-500">RAM</dt>
                <dd className="text-sm font-medium">{device.ram}</dd>
              </div>
            )}
            {device.storage && (
              <div>
                <dt className="text-sm text-gray-500">Depolama</dt>
                <dd className="text-sm font-medium">{device.storage}</dd>
              </div>
            )}
            {device.graphicsCard && (
              <div>
                <dt className="text-sm text-gray-500">Ekran Kartı</dt>
                <dd className="text-sm font-medium">{device.graphicsCard}</dd>
              </div>
            )}
            {device.operatingSystem && (
              <div>
                <dt className="text-sm text-gray-500">İşletim Sistemi</dt>
                <dd className="text-sm font-medium">{device.operatingSystem}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Lokasyon</dt>
              <dd className="text-sm font-medium">{device.location || '-'}</dd>
            </div>
          </dl>
        </div>

        {/* Purchase & Lifespan Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Satın Alma & Ömür</h2>
          <dl className="grid grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Alım Tarihi</dt>
              <dd className="text-sm font-medium">
                {device.purchaseDate ? format(new Date(device.purchaseDate), 'dd MMMM yyyy', { locale: tr }) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Alım Fiyatı</dt>
              <dd className="text-sm font-medium">{device.purchasePrice ? `${device.purchasePrice} TL` : '-'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Garanti Bitiş</dt>
              <dd className="text-sm font-medium">
                {device.warrantyEndDate ? format(new Date(device.warrantyEndDate), 'dd MMMM yyyy', { locale: tr }) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Beklenen Ömür</dt>
              <dd className="text-sm font-medium">{device.lifespanMonths ? `${device.lifespanMonths} ay` : '-'}</dd>
            </div>
            <div className="col-span-2">
              <dt className="text-sm text-gray-500">Kalan Ömür</dt>
              <dd className={`text-lg font-bold ${device.remainingLifespan === 0 ? 'text-red-600' : device.remainingLifespan <= 6 ? 'text-yellow-600' : 'text-green-600'}`}>
                {device.remainingLifespan !== null ? `${device.remainingLifespan} ay` : '-'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Assignment Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Zimmet Bilgisi</h2>
          {device.assignedUser ? (
            <dl className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm text-gray-500">Kullanıcı</dt>
                <dd className="text-sm font-medium">{device.assignedUser.fullName}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Departman</dt>
                <dd className="text-sm font-medium">{device.assignedUser.department || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Lokasyon</dt>
                <dd className="text-sm font-medium">{device.assignedUser.location || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Zimmet Tarihi</dt>
                <dd className="text-sm font-medium">
                  {device.assignmentDate ? format(new Date(device.assignmentDate), 'dd MMMM yyyy', { locale: tr }) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Email</dt>
                <dd className="text-sm font-medium">{device.assignedUser.email}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Telefon</dt>
                <dd className="text-sm font-medium">{device.assignedUser.phone || '-'}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-gray-500">Bu cihaz kimseye zimmetli değil</p>
          )}
        </div>

        {/* Installed Software */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Yüklü Yazılımlar</h2>
            <button
              onClick={() => setShowSoftwareModal(true)}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Ekle
            </button>
          </div>
          {device.installedSoftware?.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {device.installedSoftware.map((software) => (
                <li key={software.id} className="py-3 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{software.name}</p>
                    <p className="text-sm text-gray-500">
                      {software.version && `v${software.version}`}
                      {software.installDate && ` - ${format(new Date(software.installDate), 'dd.MM.yyyy')}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveSoftware(software.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">Kayıtlı yazılım yok</p>
          )}
        </div>
      </div>

      {/* Zimmet Belgeleri */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Zimmet Belgeleri</h2>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-1" />
            Belge Yükle
          </button>
        </div>
        {documents.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <li key={doc.id} className="py-3 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <DocumentIcon className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="font-medium">{doc.originalName}</p>
                    <p className="text-sm text-gray-500">
                      {doc.documentType === 'assignment' && 'Zimmet Formu'}
                      {doc.documentType === 'return' && 'İade Formu'}
                      {doc.documentType === 'other' && 'Diğer'}
                      {' - '}
                      {format(new Date(doc.createdAt), 'dd.MM.yyyy HH:mm')}
                      {doc.uploadedBy && ` - ${doc.uploadedBy.fullName}`}
                    </p>
                    {doc.description && <p className="text-xs text-gray-400">{doc.description}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownloadDocument(doc)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="İndir"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteDocument(doc.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Sil"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">Kayıtlı belge yok</p>
        )}
      </div>

      {/* Assignment History */}
      {device.assignmentHistory?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-lg font-semibold mb-4">Zimmet Geçmişi</h2>
          <ul className="divide-y divide-gray-200">
            {device.assignmentHistory.map((history) => (
              <li key={history.id} className="py-3">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">
                      {history.action === 'assigned' && `${history.user?.fullName}'e zimmetlendi`}
                      {history.action === 'unassigned' && `${history.user?.fullName}'den alındı`}
                      {history.action === 'retired_available' && 'Boşa çıkarıldı'}
                      {history.action === 'retired_hek' && 'HEK\'e gönderildi'}
                      {history.action === 'retired_donation' && 'Bağışlandı'}
                    </p>
                    {history.description && <p className="text-sm text-gray-500">{history.description}</p>}
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>{format(new Date(history.assignmentDate), 'dd.MM.yyyy')}</p>
                    <p className="text-xs">by {history.assignedBy?.fullName}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Cihazı Zimmetle</h3>
            <form onSubmit={handleAssign}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kullanıcı</label>
                  <select
                    required
                    value={assignData.userId}
                    onChange={(e) => setAssignData({ ...assignData, userId: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Seçiniz</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.fullName} - {user.department}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Zimmet Tarihi</label>
                  <input
                    type="date"
                    value={assignData.assignmentDate}
                    onChange={(e) => setAssignData({ ...assignData, assignmentDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    value={assignData.description}
                    onChange={(e) => setAssignData({ ...assignData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="2"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Zimmetle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Unassign Modal */}
      {showUnassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Zimmetten Düşür</h3>
            <form onSubmit={handleUnassign}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sebep</label>
                  <select
                    required
                    value={unassignData.reason}
                    onChange={(e) => setUnassignData({ ...unassignData, reason: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Seçiniz</option>
                    <option value="available">Boşa Çıkar</option>
                    <option value="hek">HEK (Hurda)</option>
                    <option value="donation">Bağış</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama (Zorunlu)</label>
                  <textarea
                    required
                    value={unassignData.description}
                    onChange={(e) => setUnassignData({ ...unassignData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                    placeholder="Lütfen açıklama giriniz..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowUnassignModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Zimmetten Düşür
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Software Modal */}
      {showSoftwareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Yazılım Ekle</h3>
            <form onSubmit={handleAddSoftware}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Yazılım Adı</label>
                  <input
                    type="text"
                    required
                    value={softwareData.name}
                    onChange={(e) => setSoftwareData({ ...softwareData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Versiyon</label>
                  <input
                    type="text"
                    value={softwareData.version}
                    onChange={(e) => setSoftwareData({ ...softwareData, version: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Lisans Anahtarı</label>
                  <input
                    type="text"
                    value={softwareData.licenseKey}
                    onChange={(e) => setSoftwareData({ ...softwareData, licenseKey: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kurulum Tarihi</label>
                  <input
                    type="date"
                    value={softwareData.installDate}
                    onChange={(e) => setSoftwareData({ ...softwareData, installDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowSoftwareModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Belge Yükle</h3>
            <form onSubmit={handleUploadDocument}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dosya</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 10MB)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Belge Türü</label>
                  <select
                    value={uploadData.documentType}
                    onChange={(e) => setUploadData({ ...uploadData, documentType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="assignment">Zimmet Formu</option>
                    <option value="return">İade Formu</option>
                    <option value="other">Diğer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="2"
                    placeholder="İsteğe bağlı açıklama..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadData({ file: null, documentType: 'assignment', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {uploading ? 'Yükleniyor...' : 'Yükle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ArrowsRightLeftIcon className="h-6 w-6 text-purple-600" />
                Zimmet Devri
              </h3>

              {/* Mevcut Zimmet Bilgisi */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Mevcut Zimmetli</h4>
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium text-lg">
                    {device.assignedUser?.fullName?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{device.assignedUser?.fullName}</p>
                    <p className="text-sm text-gray-500">
                      {device.assignedUser?.departmentInfo?.name || device.assignedUser?.department || 'Departman belirtilmemiş'}
                      {device.assignedUser?.location && ` - ${device.assignedUser.location}`}
                    </p>
                    <p className="text-xs text-gray-400">
                      Zimmet: {device.assignmentDate ? format(new Date(device.assignmentDate), 'dd MMMM yyyy', { locale: tr }) : '-'}
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleTransfer}>
                <div className="space-y-4">
                  {/* Yeni Kullanıcı Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Devredilecek Kişi *</label>
                    <select
                      required
                      value={transferData.newUserId}
                      onChange={(e) => setTransferData({ ...transferData, newUserId: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Kişi seçiniz</option>
                      {users
                        .filter(u => u.id !== device.assignedUser?.id)
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.fullName} - {user.departmentInfo?.name || user.department || 'Departman yok'}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Devir Sebebi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Devir Sebebi *</label>
                    <select
                      required
                      value={transferData.reason}
                      onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Sebep seçiniz</option>
                      <option value="İşten ayrılma">İşten ayrılma</option>
                      <option value="Departman değişikliği">Departman değişikliği</option>
                      <option value="Pozisyon değişikliği">Pozisyon değişikliği</option>
                      <option value="Cihaz yükseltme">Cihaz yükseltme</option>
                      <option value="Cihaz değişimi">Cihaz değişimi</option>
                      <option value="Geçici görevlendirme">Geçici görevlendirme</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  {/* Devir Tarihi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Devir Tarihi</label>
                    <input
                      type="date"
                      value={transferData.transferDate}
                      onChange={(e) => setTransferData({ ...transferData, transferDate: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  {/* Açıklama */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ek Açıklama</label>
                    <textarea
                      value={transferData.description}
                      onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      rows="2"
                      placeholder="İsteğe bağlı ek bilgi..."
                    />
                  </div>

                  {/* Belge Yükleme */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Zimmet Belgesi (İsteğe Bağlı)</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setTransferData({ ...transferData, documentFile: e.target.files[0] })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Yeni zimmet belgesi yükleyebilirsiniz. Eski belgeler otomatik silinecektir.
                    </p>
                  </div>
                </div>

                {/* Zimmet Geçmişi */}
                {device.assignmentHistory?.length > 0 && (
                  <div className="mt-6 border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Son Zimmet Geçmişi</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {device.assignmentHistory.slice(0, 5).map((history) => (
                        <div key={history.id} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                          <span className="text-gray-600">
                            {history.action === 'assigned' && `${history.user?.fullName}'e zimmetlendi`}
                            {history.action === 'unassigned' && `${history.user?.fullName}'den alındı`}
                          </span>
                          <span className="text-gray-400">
                            {format(new Date(history.assignmentDate), 'dd.MM.yyyy')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTransferModal(false);
                      setTransferData({
                        newUserId: '',
                        reason: '',
                        description: '',
                        transferDate: new Date().toISOString().split('T')[0],
                        documentFile: null
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={transferring}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {transferring ? 'Devrediliyor...' : 'Zimmeti Devret'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-center">Cihaz QR Kodu</h3>
            <div ref={qrRef} className="flex flex-col items-center">
              <QRCodeSVG
                value={`${window.location.origin}/devices/${device.id}`}
                size={200}
                level="H"
                includeMargin={true}
              />
              <div className="mt-4 text-center">
                <p className="font-medium">{device.name}</p>
                <p className="text-sm text-gray-500">{device.serialNumber || device.barcode || '-'}</p>
                <p className="text-xs text-gray-400 mt-1">{device.brand} {device.model}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowQRModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Kapat
              </button>
              <button
                onClick={handleDownloadQR}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                PNG Indir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceDetail;
